<template>
    <div class="container admin-table-page">
        <!-- Fila del título y flecha hacia atrás -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="d-flex align-items-center">
                    <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
                        <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" class="back-icon" />
                    </b-button>
                    <h2 class="param-title">{{ labels.title }}</h2>
                </div>
            </div>
        </div>

        <!-- Fila de filtros debajo del título a la izquierda -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100" style="flex-wrap: wrap; gap: 8px;">
                <!-- ============================================ -->
                <!-- FILTROS PRINCIPALES - OCULTOS EN VISTA DETALLE -->
                <!-- ============================================ -->
                <div v-if="!showDetailView" class="filters-container d-flex align-items-center">
                    <b-form-select
                        id="validity-select"
                        v-model="selVigencia"
                        class="filter-select"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectValidity }}
                        </b-form-select-option>
                        <option v-for="item in listVigencia" :key="item.vigencia" :value="item.vigencia">
                            {{ item?.vigencia }}
                        </option>
                    </b-form-select>
                    
                    <b-button
                        class="consult-btn ml-2"
                        @click="consultAjuste()"
                        :disabled="!selVigencia"
                    >
                        <font-awesome-icon :icon="['fas', 'search']" class="mr-1" />
                        {{ labels.consultar }}
                    </b-button>
                </div>

                <!-- ============================================ -->
                <!-- BOTONES ORIGEN / DESTINO + NOMBRE COMPAÑÍA -->
                <!-- ============================================ -->
                <div v-if="showDetailView" class="detail-header d-flex align-items-center w-100">
                    <div class="detail-tabs-buttons d-flex align-items-center">
                        <b-button
                            :class="activeTab === 'origen' ? 'tab-btn-origin-dest tab-active' : 'tab-btn-origin-dest tab-inactive'"
                            @click="activeTab = 'origen'"
                            :disabled="false"
                        >
                            <b-icon icon="arrow-right-circle" class="mr-1"></b-icon>
                            {{ labels.origin }}
                            <b-badge :variant="activeTab === 'origen' ? 'light' : 'secondary'" class="ml-1">
                                {{ ajusteOrig ? ajusteOrig.length : 0 }}
                            </b-badge>
                        </b-button>
                        
                        <!-- SEPARADOR ENTRE BOTONES -->
                        <div class="tab-separator"></div>
                        
                        <b-button
                            :class="activeTab === 'destino' ? 'tab-btn-origin-dest tab-active' : 'tab-btn-origin-dest tab-inactive'"
                            @click="activeTab = 'destino'"
                            :disabled="false"
                        >
                            <b-icon icon="arrow-left-circle" class="mr-1"></b-icon>
                            {{ labels.destination }}
                            <b-badge :variant="activeTab === 'destino' ? 'light' : 'secondary'" class="ml-1">
                                {{ ajusteDesti ? ajusteDesti.length : 0 }}
                            </b-badge>
                        </b-button>
                    </div>

                    <!-- NOMBRE DE LA COMPAÑÍA - EXTREMO DERECHO -->
                    <div class="detail-company-name ml-auto">
                        <span v-if="activeTab === 'origen' && nombreCompaniaOrigen" class="company-name-text">
                            <font-awesome-icon :icon="['fas', 'building']" class="mr-1" />
                            {{ nombreCompaniaOrigen }}
                        </span>
                        <span v-else-if="activeTab === 'destino' && nombreCompaniaDestino" class="company-name-text">
                            <font-awesome-icon :icon="['fas', 'building']" class="mr-1" />
                            {{ nombreCompaniaDestino }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <div class="admin-table-block mt-4">
            <!-- ============================================ -->
            <!-- ESTADO INICIAL - Sin datos -->
            <!-- ============================================ -->
            <div v-if="!isLoading && !showDetailView && !showToolbarExtras && ajustes.length === 0">
                <CorporateDataTable
                    :items="[]"
                    :fields="[]"
                    :labels="tableLabels"
                    :loading="false"
                    :default-per-page="10"
                    :page-options="[10]"
                    :narrow="false"
                    :table-min-height="400"
                    :table-max-height="500"
                    :show-search="false"
                    :allow-column-toggle="false"
                    :enable-sorting="false"
                    :show-pagination="false"
                    :show-rows-per-page="false"
                >
                    <template #empty-state>
                        <div class="text-center py-5">
                            <font-awesome-icon
                                :icon="['fas', 'search']"
                                size="3x"
                                class="text-muted mb-3"
                                style="opacity: 0.3;"
                            />
                            <p class="h4 text-muted">
                                {{ labels.selectFilters }}
                            </p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- ============================================ -->
            <!-- SECCIÓN: TABLA PRINCIPAL DE AJUSTES -->
            <!-- ============================================ -->
            <div v-if="!isLoading && !showDetailView && showToolbarExtras && ajustes.length > 0">
                <CorporateDataTable
                    :items="ajustes"
                    :fields="allFields"
                    :labels="tableLabels"
                    :default-per-page="10"
                    :page-options="[10, 20, 50, 100]"
                    :narrow="false"
                    :table-min-height="500"
                    :table-max-height="500"
                    :show-search="true"
                    :allow-column-toggle="true"
                    :enable-sorting="true"
                    :filter-defs="tableFilterDefs"
                    :toolbar-disabled="false"
                    class="ajustes-table-wrapper"
                >
                    <template #toolbar-actions>
                        <div class="niif-toolbar-right">
                            <b-button
                                size="sm"
                                class="table-toolbar-add-btn"
                                @click="adicionAjuste()"
                                :disabled="isVigenciaBloqueada"
                            >
                                {{ labels.crear }}
                            </b-button>
                        </div>
                    </template>

                    <template #cell(secuencia)="data">
                        <div class="text-center"><strong>{{ data.item?.secuencia }}</strong></div>
                    </template>

                    <template #cell(tipo_ajuste)="data">
                        <div class="text-left">{{ tipoAjuste(data.item?.tipo_ajuste) }}</div>
                    </template>

                    <template #cell(origen_nombre)="data">
                        <div class="text-left">{{ data.item?.origen_nombre }}</div>
                    </template>

                    <template #cell(tipo_origen)="data">
                        <div class="text-left">{{ data.item?.tipo_origen }}</div>
                    </template>

                    <template #cell(destino_nombre)="data">
                        <div class="text-left">{{ data.item?.destino_nombre }}</div>
                    </template>

                    <template #cell(tipo_destino)="data">
                        <div class="text-left">{{ data.item?.tipo_destino }}</div>
                    </template>

                    <template #cell(accionVer)="data">
                        <b-button
                            class="btn-info table-action-btn"
                            style="width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center;"
                            @click.stop="verDetalleAjuste(data.item)"
                            :title="labels.viewDetails"
                        >
                            <font-awesome-icon :icon="['fas', 'eye']" size="xs" />
                        </b-button>
                    </template>

                    <template #cell(accionDelete)="data">
                        <b-button
                            class="reset-btn-danger table-action-btn"
                            style="width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center;"
                            @click.stop="borraAjuste(data.item.secuencia)"
                            :disabled="isVigenciaBloqueada"
                            :title="labels.eliminar"
                        >
                            <font-awesome-icon :icon="['fas', 'trash']" size="xs" />
                        </b-button>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- ============================================ -->
            <!-- TABLA VACÍA - Consulta realizada sin resultados -->
            <!-- ============================================ -->
            <div v-if="!isLoading && !showDetailView && showToolbarExtras && ajustes.length === 0" class="text-center py-5">
                <font-awesome-icon
                    :icon="['fas', 'file-alt']"
                    size="4x"
                    class="text-muted mb-3"
                    style="opacity: 0.3;"
                />
                <p class="h4 text-muted">{{ labels.empty }}</p>
            </div>

            <!-- ============================================ -->
            <!-- SECCIÓN: VISTA DE DETALLE (TABLAS ORIGEN/DESTINO) -->
            <!-- ============================================ -->
            <div v-if="!isLoading && showDetailView">
                <!-- CONTENIDO: Tabla de Origen -->
                <div v-if="activeTab === 'origen'" class="detail-content">
                    <AjustesOrigen 
                        :key="'origen-' + selectedRow"
                        :ajustOrigen.sync="ajusteOrig" 
                        :codReporte="null" 
                        :id_compania="idCompaniaActual" 
                        :vigencia="selVigencia" 
                        :nombreCompania="nombreCompaniaOrigen" 
                        :readOnly="isVigenciaBloqueada"
                        :tableLabels="detailTableLabels"
                        :tableFilterDefs="detailFilterDefs"
                        :defaultPerPage="10"
                        :pageOptions="[10, 20, 50, 100]"
                        :showBackButton="true"
                        :showCompanyName="false"
                        :secuencia="selectedRow"
                        :tipo="tipoOrigenActual"
                        :tipoAjuste="tipoAjusteActual"
                        @update:ajustOrigen="handleOrigenUpdate"
                        @back="cerrarDetalle"
                    />
                </div>

                <!-- CONTENIDO: Tabla de Destino -->
                <div v-if="activeTab === 'destino'" class="detail-content">
                    <AjustesDestino 
                        :key="'destino-' + selectedRow"
                        :ajustDestino.sync="ajusteDesti" 
                        :codReporte="null" 
                        :id_compania="idCompaniaDestinoActual" 
                        :vigencia="selVigencia" 
                        :nombreCompania="nombreCompaniaDestino" 
                        :readOnly="isVigenciaBloqueada"
                        :tableLabels="detailTableLabels"
                        :tableFilterDefs="detailFilterDefs"
                        :defaultPerPage="10"
                        :pageOptions="[10, 20, 50, 100]"
                        :showBackButton="true"
                        :showCompanyName="false"
                        :secuencia="selectedRow"
                        :tipo="tipoDestinoActual"
                        :tipoAjuste="tipoAjusteActual"
                        @update:ajustDestino="handleDestinoUpdate"
                        @back="cerrarDetalle"
                    />
                </div>
            </div>

            <!-- ============================================ -->
            <!-- SPINNER DE CARGA -->
            <!-- ============================================ -->
            <div v-if="isLoading" class="text-center py-5">
                <b-spinner></b-spinner>
                <p class="mt-2">{{ labels.loading }}</p>
            </div>
        </div>

        <!-- Modal Crear Ajuste -->
        <NewAjuste 
            :showNewAjuste="showNewAjuste" 
            :vigenciaSelect="selVigencia" 
            @reloadTbl="refrescaTbl"
            @closeModal="cierraModal" 
        />
    </div>
</template>

<script>
import PlanCuentas from '../../services/PlanCuentas' 
import Ajustes from '../../services/AjustesConsol' 
import NewAjuste from './NewAjuste.vue' 
import AjustesOrigen from './AjustesOrigen.vue' 
import AjustesConsol from '../../services/AjustesConsol' 
import AjustesDestino from './AjustesDestino.vue' 
import CorporateDataTable from '../utils/CorporateDataTable.vue'

export default {
    name: "AjustesConsol",
    components: {
        NewAjuste,
        AjustesOrigen,
        AjustesDestino,
        CorporateDataTable
    },
    data: () => ({
        showToolbarExtras: false,
        selVigencia: "",
        reportes: [],
        listVigencia: [],
        ajustes: [],
        ajusteOrig: [],
        ajusteDesti: [],
        showNewAjuste: false,
        showDetailView: false,
        activeTab: 'origen',
        generado: false,
        selectedRow: null,
        isLoading: false,
        nombreCompaniaOrigen: '',
        nombreCompaniaDestino: '',
        idCompaniaActual: null,
        idCompaniaDestinoActual: null,
        // Guardar tipo y tipo_ajuste para pasarlos a los hijos
        tipoOrigenActual: null,
        tipoDestinoActual: null,
        tipoAjusteActual: null,
    }),
    computed: {
        isVigenciaBloqueada() {
            return this.selVigencia ? !this.$store.getters.isPeriodoEnabled(this.selVigencia) : false
        },
        labels() {
            if (this.$store.state._language == "es-ES") {
                return {
                    title: "Ajustes de consolidación",
                    selectValidity: "Seleccione Vigencia",
                    consultar: "Consultar",
                    vigencia: "Vigencia",
                    reporte: "Reporte",
                    empty: "No hay registros disponibles",
                    crear: "Crear Ajuste",
                    secuencia: "Secuencia",
                    tipo_ajuste: "Tipo ajuste",
                    origen_nombre: "Empresa Origen",
                    tipo_origen: "Tipo Origen",
                    destino_nombre: "Empresa Destino",
                    tipo_destino: "Tipo Destino",
                    eliminar: "Eliminar",
                    ver: "Ver",
                    viewDetails: "Ver detalles del ajuste",
                    selectedRow: "Ajuste seleccionado",
                    sequence: "Secuencia",
                    loading: "Cargando...",
                    noData: "No hay datos disponibles",
                    noMatches: "No se encontraron resultados",
                    searchPlaceholder: "Buscar en la tabla...",
                    rowsPerPage: "Filas por página:",
                    noSort: "Sin orden",
                    sortAsc: "A-Z / Menor a mayor",
                    sortDesc: "Z-A / Mayor a menor",
                    columns: "Columnas",
                    showAllColumns: "Mostrar todas",
                    filters: "Filtros",
                    clearFilters: "Limpiar filtros",
                    close: "Cerrar",
                    backToList: "Volver a la lista",
                    origin: "Origen",
                    destination: "Destino",
                    noOriginData: "No hay cuentas de origen registradas",
                    noDestinationData: "No hay cuentas de destino registradas",
                    volver: "Volver",
                    selectFilters: "Seleccione la vigencia y consulte"
                }
            } else {
                return {
                    title: "Consolidation adjustments",
                    selectValidity: "Select Validity",
                    consultar: "Consult",
                    vigencia: "Validity",
                    reporte: "Report",
                    empty: "No records available",
                    crear: "Create Adjustment",
                    secuencia: "Sequence",
                    tipo_ajuste: "Adjustment type",
                    origen_nombre: "Origin Company",
                    tipo_origen: "Origin Type",
                    destino_nombre: "Destination Company",
                    tipo_destino: "Destination Type",
                    eliminar: "Delete",
                    ver: "View",
                    viewDetails: "View adjustment details",
                    selectedRow: "Selected adjustment",
                    sequence: "Sequence",
                    loading: "Loading...",
                    noData: "No data available",
                    noMatches: "No results found",
                    searchPlaceholder: "Search in table...",
                    rowsPerPage: "Rows per page:",
                    noSort: "No sorting",
                    sortAsc: "A-Z / Low to high",
                    sortDesc: "Z-A / High to low",
                    columns: "Columns",
                    showAllColumns: "Show all",
                    filters: "Filters",
                    clearFilters: "Clear filters",
                    close: "Close",
                    backToList: "Back to list",
                    origin: "Origin",
                    destination: "Destination",
                    noOriginData: "No origin accounts registered",
                    noDestinationData: "No destination accounts registered",
                    volver: "Back",
                    selectFilters: "Select validity and consult"
                }
            }
        },
        allFields() {
            return [
                { key: 'secuencia', label: this.labels.secuencia, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle', minWidth: '100px' },
                { key: 'tipo_ajuste', label: this.labels.tipo_ajuste, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle', minWidth: '180px' },
                { key: 'origen_nombre', label: this.labels.origen_nombre, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle', minWidth: '200px' },
                { key: 'tipo_origen', label: this.labels.tipo_origen, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle', minWidth: '150px' },
                { key: 'destino_nombre', label: this.labels.destino_nombre, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle', minWidth: '200px' },
                { key: 'tipo_destino', label: this.labels.tipo_destino, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle', minWidth: '150px' },
                { key: 'accionVer', label: this.labels.ver, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false, minWidth: '60px' },
                { key: 'accionDelete', label: this.labels.eliminar, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false, minWidth: '60px' }
            ]
        },
        tableFilterDefs() {
            return [
                { key: 'secuencia', type: 'text', placeholder: this.$store.state._language == "es-ES" ? 'Filtrar secuencia...' : 'Filter sequence...' },
                { key: 'tipo_ajuste', type: 'text', placeholder: this.$store.state._language == "es-ES" ? 'Filtrar tipo ajuste...' : 'Filter adjustment type...' },
                { key: 'origen_nombre', type: 'text', placeholder: this.$store.state._language == "es-ES" ? 'Filtrar empresa origen...' : 'Filter origin company...' },
                { key: 'tipo_origen', type: 'text', placeholder: this.$store.state._language == "es-ES" ? 'Filtrar tipo origen...' : 'Filter origin type...' },
                { key: 'destino_nombre', type: 'text', placeholder: this.$store.state._language == "es-ES" ? 'Filtrar empresa destino...' : 'Filter destination company...' },
                { key: 'tipo_destino', type: 'text', placeholder: this.$store.state._language == "es-ES" ? 'Filtrar tipo destino...' : 'Filter destination type...' }
            ]
        },
        tableLabels() {
            return {
                noRow: this.labels.noData,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        },
        detailTableLabels() {
            return {
                noRow: this.labels.noData,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters,
                adicionarCuenta: "Adición Cuenta",
                adicionarCuentaNueva: "Adición Cuenta Nueva"
            }
        },
        detailFilterDefs() {
            return [
                { key: 'cuenta', type: 'text', placeholder: this.$store.state._language == "es-ES" ? 'Filtrar cuenta...' : 'Filter account...' },
                { key: 'descripcion', type: 'text', placeholder: this.$store.state._language == "es-ES" ? 'Filtrar descripción...' : 'Filter description...' },
                { key: 'valor', type: 'number', placeholder: this.$store.state._language == "es-ES" ? 'Filtrar valor...' : 'Filter value...' }
            ]
        }
    },
    methods: {
        goBack() {
            this.$router.back()
        },

        async consultAjuste() {
            if (!this.selVigencia) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'Debe seleccionar vigencia primero' : 'Please select a validity first');
                return;
            }
            this.isLoading = true;
            this.cerrarDetalle();
            
            const codReportePrincipal = await this.getReportePrincipal();
            if (!codReportePrincipal) {
                this.isLoading = false;
                return;
            }
            
            Ajustes.getAjustesConsol(codReportePrincipal, this.selVigencia)
                .then((result) => {
                    this.ajustes = result.data.ajustes || [];
                    this.showToolbarExtras = true;
                })
                .catch(() => {
                    this.ajustes = [];
                    this.showToolbarExtras = true;
                    this.$toast.error(this.$store.state._language == "es-ES" ? 'Error al consultar los ajustes' : 'Error consulting adjustments');
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        tipoAjuste(tipoAjus) {
            if (tipoAjus == 1) {
                return this.$store.state._language == "es-ES" ? 'Interes no controlante' : 'Non-controlling interest';
            } else if (tipoAjus == 2) {
                return this.$store.state._language == "es-ES" ? 'Eliminación en inversión' : 'Investment elimination';
            } else if (tipoAjus == 3) {
                return this.$store.state._language == "es-ES" ? 'Eliminación saldo recíproco' : 'Reciprocal balance elimination';
            } else if (tipoAjus == 4) {
                return this.$store.state._language == "es-ES" ? 'Ajuste del Combinado' : 'Combined adjustment';
            }
            return this.$store.state._language == "es-ES" ? 'Tipo desconocido' : 'Unknown type';
        },

        async verDetalleAjuste(item) {
            // Guardar todos los datos del ajuste seleccionado
            this.selectedRow = item.secuencia;
            this.idCompaniaActual = item.id_compania_origen || item.origen;
            this.idCompaniaDestinoActual = item.id_compania_destino || item.destino;
            this.tipoOrigenActual = item.tipo_origen || null;
            this.tipoDestinoActual = item.tipo_destino || null;
            this.tipoAjusteActual = item.tipo_ajuste || null;
            
            // Mostrar la vista de detalle inmediatamente
            this.showDetailView = true;
            this.activeTab = 'origen';
            
            // Cargar los datos en segundo plano
            await this.infoAjuste(item.secuencia, item.origen, item.destino);
        },

        cerrarDetalle() {
            this.showDetailView = false;
            this.selectedRow = null;
            this.ajusteOrig = [];
            this.ajusteDesti = [];
            this.nombreCompaniaOrigen = '';
            this.nombreCompaniaDestino = '';
            this.idCompaniaActual = null;
            this.idCompaniaDestinoActual = null;
            this.tipoOrigenActual = null;
            this.tipoDestinoActual = null;
            this.tipoAjusteActual = null;
            this.activeTab = 'origen';
        },

        handleOrigenUpdate(newData) {
            this.ajusteOrig = newData;
            // Si los datos de origen tienen información, actualizar el ID de compañía
            if (newData && newData.length > 0 && newData[0].id_compania) {
                this.idCompaniaActual = newData[0].id_compania;
            }
        },

        handleDestinoUpdate(newData) {
            this.ajusteDesti = newData;
            // Si los datos de destino tienen información, actualizar el ID de compañía
            if (newData && newData.length > 0 && newData[0].id_compania) {
                this.idCompaniaDestinoActual = newData[0].id_compania;
            }
        },

        async adicionAjuste() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'La vigencia seleccionada está deshabilitada para crear o editar.' : 'The selected validity is disabled for creating or editing.');
                return;
            }
            if (!this.selVigencia) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'Debe seleccionar vigencia primero' : 'Please select a validity first');
                return;
            }
            const codReportePrincipal = await this.getReportePrincipal();
            if (!codReportePrincipal) {
                return;
            }
            this.showNewAjuste = true;
            this.cerrarDetalle();
        },

        async refrescaTbl(vigencia) {
            this.isLoading = true;
            const codReportePrincipal = await this.getReportePrincipal();
            if (!codReportePrincipal) {
                this.isLoading = false;
                return;
            }
            
            Ajustes.getAjustesConsol(codReportePrincipal, vigencia)
                .then((result) => {
                    this.ajustes = result.data.ajustes || [];
                    this.showNewAjuste = false;
                    this.cerrarDetalle();
                    this.showToolbarExtras = true;
                })
                .catch(() => {
                    this.$toast.error(this.$store.state._language == "es-ES" ? 'Error al refrescar la tabla' : 'Error refreshing table');
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        cierraModal(val) {
            this.showNewAjuste = val;
        },

        async infoAjuste(id, idCompOrig, idCompDest) {
            try {
                this.isLoading = true;
                const codReportePrincipal = await this.getReportePrincipal();
                if (!codReportePrincipal) {
                    this.isLoading = false;
                    return;
                }

                // Obtener datos de origen
                try {
                    let ajusteOrig = await AjustesConsol.consulAjusteOrigen(id, this.selVigencia, idCompOrig, codReportePrincipal);
                    this.ajusteOrig = ajusteOrig.data.ajustOrig || [];
                    // Actualizar el ID de compañía si los datos tienen información
                    if (this.ajusteOrig && this.ajusteOrig.length > 0 && this.ajusteOrig[0].id_compania) {
                        this.idCompaniaActual = this.ajusteOrig[0].id_compania;
                    }
                } catch (error) {
                    console.error('Error al cargar origen:', error);
                    this.ajusteOrig = [];
                }

                // Obtener datos de destino
                try {
                    let ajusteDest = await AjustesConsol.consulAjusteDestino(id, this.selVigencia, codReportePrincipal);
                    this.ajusteDesti = ajusteDest.data.ajustDest || [];
                    // Actualizar el ID de compañía si los datos tienen información
                    if (this.ajusteDesti && this.ajusteDesti.length > 0 && this.ajusteDesti[0].id_compania) {
                        this.idCompaniaDestinoActual = this.ajusteDesti[0].id_compania;
                    }
                } catch (error) {
                    console.error('Error al cargar destino:', error);
                    this.ajusteDesti = [];
                }

                // Obtener nombres de compañía
                if (idCompOrig) {
                    this.nombreCompaniaOrigen = await this.obtenerNombreCompania(idCompOrig);
                } else {
                    this.nombreCompaniaOrigen = '';
                }

                if (idCompDest) {
                    this.nombreCompaniaDestino = await this.obtenerNombreCompania(idCompDest);
                } else {
                    this.nombreCompaniaDestino = '';
                }

                // Determinar qué pestaña activar basado en los datos disponibles
                if (this.activeTab === 'origen') {
                    if (!this.ajusteOrig || this.ajusteOrig.length === 0) {
                        if (this.ajusteDesti && this.ajusteDesti.length > 0) {
                            this.activeTab = 'destino';
                        }
                    }
                } else if (this.activeTab === 'destino') {
                    if (!this.ajusteDesti || this.ajusteDesti.length === 0) {
                        if (this.ajusteOrig && this.ajusteOrig.length > 0) {
                            this.activeTab = 'origen';
                        }
                    }
                }

                this.showDetailView = true;
            } catch (error) {
                console.error('Error en infoAjuste:', error);
                this.$toast.error(this.$store.state._language == "es-ES" ? 'Error al cargar la información del ajuste' : 'Error loading adjustment information');
            } finally {
                this.isLoading = false;
            }
        },

        async obtenerNombreCompania(idCompania) {
            try {
                const response = await AjustesConsol.getCompanyName(idCompania);
                return response.data?.nombre || `Compañía ${idCompania}`;
            } catch (error) {
                console.error('Error al obtener nombre de compañía:', error);
                return `Compañía ${idCompania}`;
            }
        },

        async getReportePrincipal() {
            try {
                const response = await Ajustes.getReportePrincipal();
                return response.data?.codigo;
            } catch (error) {
                console.error('Error al obtener reporte principal:', error);
                this.$toast.error(this.$store.state._language == "es-ES" ? 'Error al cargar el reporte principal' : 'Error loading main report');
                return null;
            }
        },

        async borraAjuste(secuencia) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'La vigencia seleccionada está deshabilitada para eliminar.' : 'The selected validity is disabled for deleting.');
                return;
            }
            if (!confirm(this.$store.state._language == "es-ES" ? '¿Está seguro de que desea eliminar este ajuste?' : 'Are you sure you want to delete this adjustment?')) {
                return;
            }
            
            this.isLoading = true;
            Ajustes.borraAjuste(this.selVigencia, secuencia)
                .then((res) => {
                    if (res.status == 200) {
                        this.$toast.success(this.$store.state._language == "es-ES" ? 'Eliminación exitosa' : 'Deletion successful');
                        if (this.selectedRow === secuencia) {
                            this.cerrarDetalle();
                        }
                        this.refrescaTbl(this.selVigencia);
                    } else {
                        this.$toast.error(this.$store.state._language == "es-ES" ? 'Error al eliminar' : 'Error deleting');
                    }
                })
                .catch((err) => {
                    console.log(err);
                    this.$toast.error(this.$store.state._language == "es-ES" ? 'Error al eliminar el ajuste' : 'Error deleting adjustment');
                })
                .finally(() => {
                    this.isLoading = false;
                });
        }
    },
    watch: {
        selVigencia(val) {
            if (val && this.isVigenciaBloqueada) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'La vigencia seleccionada está deshabilitada para crear o editar.' : 'The selected validity is disabled for creating or editing.');
            }
            if (val == "") {
                this.ajustes = [];
                this.cerrarDetalle();
                this.showToolbarExtras = false;
            }
        }
    },
    mounted() {
        this.isLoading = true;
        PlanCuentas.getVigenciaPlancuentas()
            .then((result) => {
                this.listVigencia = result.data.getVigencia || [];
            })
            .catch((err) => {
                console.log(err);
                this.$toast.error(this.$store.state._language == "es-ES" ? 'Error al cargar las vigencias' : 'Error loading validities');
            })
            .finally(() => {
                this.isLoading = false;
            });
    }
}
</script>

<style src="../main.css"></style>

<style scoped>
/* ========== ESTILOS DE FILTROS PRINCIPALES ========== */
.niif-header .filters-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: 0 !important;
}

.niif-header .filter-select {
    min-width: 200px;
    max-width: 280px;
    width: auto;
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
}

.niif-header .filter-select option {
    white-space: normal;
    word-break: break-word;
    padding: 4px 8px;
}

.niif-header .consult-btn {
    height: 38px;
    min-height: 38px;
    padding: 0 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 14px;
    background-color: #6c757d !important;
    color: white !important;
    border: 1px solid #6c757d !important;
    cursor: pointer;
    flex-shrink: 0;
}

.niif-header .consult-btn:hover,
.niif-header .consult-btn:focus,
.niif-header .consult-btn:active,
.niif-header .consult-btn:not(:disabled):hover,
.niif-header .consult-btn:not(:disabled):focus,
.niif-header .consult-btn:not(:disabled):active {
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
    box-shadow: none !important;
}

.niif-header .consult-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
}

/* ========== HEADER DE DETALLE ========== */
.detail-header {
    display: flex;
    align-items: center;
    width: 100%;
    gap: 8px;
    flex-wrap: wrap;
}

.detail-tabs-buttons {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 0;
}

.tab-separator {
    width: 8px;
    min-width: 8px;
    flex-shrink: 0;
}

.detail-company-name {
    flex-shrink: 0;
    margin-left: auto;
    padding: 4px 12px;
    background-color: #f8f9fa;
    border-radius: 7px;
    border: 1px solid #dee2e6;
}

.company-name-text {
    font-size: 14px;
    font-weight: 600;
    color: #2c3e50;
    white-space: nowrap;
}

.company-name-text .fa-building {
    color: #6c757d;
}

.tab-btn-origin-dest {
    height: 38px;
    min-height: 38px;
    padding: 0 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    flex-shrink: 0;
    border: 1px solid #6c757d;
    transition: all 0.2s;
    background-color: transparent;
    color: #6c757d;
}

.tab-btn-origin-dest.tab-active {
    background-color: #1f4f83 !important;
    border-color: #1f4f83 !important;
    color: white !important;
}

.tab-btn-origin-dest.tab-active:hover:not(:disabled) {
    background-color: #1a446f !important;
    border-color: #1a446f !important;
}

.tab-btn-origin-dest.tab-active:focus:not(:disabled) {
    box-shadow: 0 0 0 0.2rem rgba(31, 79, 131, 0.5) !important;
}

.tab-btn-origin-dest.tab-inactive {
    background-color: transparent !important;
    border-color: #6c757d !important;
    color: #6c757d !important;
}

.tab-btn-origin-dest.tab-inactive:hover:not(:disabled) {
    background-color: #6c757d !important;
    color: white !important;
}

.tab-btn-origin-dest.tab-inactive:focus:not(:disabled) {
    box-shadow: 0 0 0 0.2rem rgba(108, 117, 125, 0.5) !important;
}

.tab-btn-origin-dest:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.tab-btn-origin-dest .badge {
    font-size: 0.7rem;
    padding: 0.2rem 0.5rem;
    border-radius: 10px;
}

.tab-btn-origin-dest.tab-active .badge {
    background-color: rgba(255, 255, 255, 0.25) !important;
    color: white !important;
}

.tab-btn-origin-dest.tab-inactive .badge {
    background-color: #6c757d !important;
    color: white !important;
}

.page-back-btn {
    padding: 0;
    font-size: 1.5rem;
    line-height: 1;
}

.back-icon {
    font-size: 1.75rem !important;
    width: 1.75rem !important;
    height: 1.75rem !important;
}

.param-title {
    font-size: 2rem !important;
    font-weight: 500;
}

.niif-toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex: 0 0 auto;
}

.table-toolbar-add-btn {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
    background-color: #1f4f83 !important;
    color: white !important;
    border: 1px solid #1f4f83 !important;
}

.table-toolbar-add-btn:hover:not(:disabled) {
    background-color: #1a446f !important;
    border-color: #1a446f !important;
}

.table-toolbar-add-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.table-action-btn {
    border: none;
    border-radius: 4px;
    transition: all 0.2s;
}

.table-action-btn.btn-info {
    background-color: #17a2b8;
    color: white;
}

.table-action-btn.btn-info:hover:not(:disabled) {
    background-color: #138496;
}

.table-action-btn.btn-info:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.table-action-btn.btn-danger {
    background-color: #dc3545;
    color: white;
}

.table-action-btn.btn-danger:hover:not(:disabled) {
    background-color: #c82333;
}

.table-action-btn.btn-danger:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.ajustes-table-wrapper {
    display: block;
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
}

.ajustes-table-wrapper .tblEstandar.tblCorporate td {
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    max-width: 0 !important;
}

.ajustes-table-wrapper .tblEstandar.tblCorporate {
    min-width: 100% !important;
    width: max-content !important;
    table-layout: fixed !important;
}

.ajustes-table-wrapper .tblEstandar.tblCorporate thead th {
    white-space: nowrap !important;
}

.table-corporate-body {
    overflow-x: auto !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch;
}

.table-corporate-body::-webkit-scrollbar {
    width: 10px;
    height: 10px;
}

.table-corporate-body::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 5px;
}

.table-corporate-body::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 5px;
}

.table-corporate-body::-webkit-scrollbar-thumb:hover {
    background: #555;
}

.table-corporate-body {
    scrollbar-width: thin;
    scrollbar-color: #888 #f1f1f1;
}

.detail-content {
    animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (max-width: 1200px) {
    .niif-header .filter-select {
        min-width: 160px;
        max-width: 220px;
        height: 36px;
        font-size: 13px;
    }
    
    .niif-header .consult-btn,
    .tab-btn-origin-dest {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
}

@media (max-width: 992px) {
    .niif-header .filters-container {
        flex-wrap: wrap;
        width: 100%;
        margin-top: 8px;
        margin-left: 0 !important;
        justify-content: flex-start;
        gap: 6px;
    }
    
    .niif-header .filter-select {
        min-width: 150px;
        max-width: 200px;
        height: 36px;
        font-size: 13px;
        flex: 1 1 auto;
    }
    
    .niif-header .consult-btn,
    .tab-btn-origin-dest {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }

    .tab-btn-origin-dest {
        flex: 1;
    }

    .detail-header {
        flex-wrap: wrap;
    }

    .detail-company-name {
        margin-left: 0 !important;
        width: 100%;
        text-align: center;
    }

    .tab-separator {
        width: 6px;
        min-width: 6px;
    }
}

@media (max-width: 576px) {
    .param-title {
        font-size: 1.5rem !important;
    }
    
    .niif-header .filter-select {
        min-width: 100%;
        max-width: 100%;
        height: 36px;
        font-size: 13px;
        flex: 0 0 100%;
    }
    
    .niif-header .filters-container {
        gap: 4px;
    }
    
    .niif-header .consult-btn,
    .tab-btn-origin-dest {
        width: 100%;
        flex: 0 0 100%;
        height: 36px;
        margin-left: 0 !important;
        margin-top: 4px;
        font-size: 13px;
        padding: 0 16px;
    }

    .tab-btn-origin-dest.ml-2 {
        margin-left: 0 !important;
    }

    .detail-tabs-buttons {
        width: 100%;
        flex-direction: column;
        align-items: stretch !important;
    }

    .tab-separator {
        display: none;
    }

    .tab-btn-origin-dest:first-child {
        margin-bottom: 4px;
    }

    .detail-company-name {
        margin-top: 8px;
        padding: 6px 12px;
    }

    .company-name-text {
        font-size: 13px;
    }
}
</style>